import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";

import {
  ColumnFiltersState,
  RowSelectionState,
  SortingState,
  VisibilityState,
  flexRender,
  getCoreRowModel,
  getFacetedRowModel,
  getFacetedUniqueValues,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
} from "@tanstack/react-table";

import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";

import { LoadingSpinner } from "../loading-spinner";
import { DataTableProps } from "./@types";
import { Actions } from "./actions";
import { Pagination } from "./pagination";
import { SearchInput } from "./search-input";
import { SkeletonRefetching } from "./skeleton";

export function DataTable<TData>({
  data,
  columns,
  inputProps,
  toolbar,
  searchFn,
  searchQuery,
  actions,
  isLoading,
}: DataTableProps<TData>) {
  const [rowSelection, setRowSelection] = useState<RowSelectionState>({});
  const [columnVisibility, setColumnVisibility] = useState<VisibilityState>({});
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([]);
  const [sorting, setSorting] = useState<SortingState>([]);
  const [searching, setSearching] = useState<boolean>(false);
  const [searchResults, setSearchResults] = useState<TData[] | null>(null);

  const TABLE_DATA = useMemo(() => data, [data]);
  const TABLE_COLUMNS = useMemo(() => columns, [columns]);

  const onSearch = async (query: string) => {
    try {
      if (!query) setSearchResults(null);
      else if (searchFn) {
        setSearching(true);
        const searchData = await searchFn(query);
        if (searchData) setSearchResults(searchData);
      }
    } catch (error) {
      toast.error((error as Error).message);
    } finally {
      setSearching(false);
    }
  };

  useEffect(() => {
    onSearch(searchQuery ?? "");

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchQuery]);

  const table = useReactTable({
    columns: TABLE_COLUMNS,
    data: searchResults !== null ? searchResults : TABLE_DATA,
    state: {
      sorting,
      columnVisibility,
      rowSelection,
      columnFilters,
    },
    initialState: { pagination: { pageSize: 6 } },
    enableRowSelection: true,
    onRowSelectionChange: setRowSelection,
    onSortingChange: setSorting,
    onColumnFiltersChange: setColumnFilters,
    onColumnVisibilityChange: setColumnVisibility,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFacetedRowModel: getFacetedRowModel(),
    getFacetedUniqueValues: getFacetedUniqueValues(),
  });

  return (
    <section className="flex flex-col flex-1 gap-4">
      {(inputProps || typeof toolbar === "function") && (
        <header className="gap-6 lg:gap-6 flex flex-col md:flex-row flex-wrap md:items-center">
          {inputProps && (
            <SearchInput
              table={table}
              inputProps={{
                name: inputProps.name,
                disabled: data.length <= 0,
                className: "md:min-w-[16rem]",
              }}
            />
          )}

          {typeof toolbar === "function" && (
            <div className="gap-4 flex-wrap md:flex-nowrap flex items-center xl:justify-end">
              {toolbar(table)}
            </div>
          )}
        </header>
      )}

      <div className="relative">
        <SkeletonRefetching show={searching} />
        <Table>
          <TableHeader>
            {table.getHeaderGroups().map((headerGroup) => (
              <TableRow key={headerGroup.id}>
                {headerGroup.headers.map((header) => {
                  return (
                    <TableHead
                      className="text-black font-semibold"
                      key={header.id}
                      colSpan={header.colSpan}
                    >
                      {header.isPlaceholder
                        ? null
                        : flexRender(
                            header.column.columnDef.header,
                            header.getContext()
                          )}
                    </TableHead>
                  );
                })}

                {actions && (
                  <TableHead className="text-black font-semibold text-center w-1">
                    Acções
                  </TableHead>
                )}
              </TableRow>
            ))}
          </TableHeader>

          {isLoading ? (
            <TableCell colSpan={columns.length} className="h-20">
              <LoadingSpinner />
            </TableCell>
          ) : (
            <TableBody>
              {table.getRowModel().rows?.length ? (
                table.getRowModel().rows.map((row) => (
                  <TableRow
                    key={row.id}
                    data-state={row.getIsSelected() && "selected"}
                  >
                    {row.getVisibleCells().map((cell) => (
                      <TableCell key={cell.id}>
                        {flexRender(
                          cell.column.columnDef.cell,
                          cell.getContext()
                        )}
                      </TableCell>
                    ))}

                    {actions && <Actions {...actions} data={row.original} />}
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell
                    colSpan={columns?.length}
                    className="h-24 text-center"
                  >
                    Nenhum resultado encontrado.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          )}
        </Table>

        <Pagination table={table} />
      </div>
    </section>
  );
}
